import React, { useEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { LogOut, User } from "lucide-react"
import type { StudentInfo } from "../../api/studentAuth"
import useLearnerAuth from "../../auth/learner/useLearnerAuth"
import { STUDENT_PROFILE_PATH } from "./config/studentNavItems"
import { getStudentInitials } from "./utils/getStudentInitials"
import { useGuardLinkClick } from "./unsavedChangesGate"
import "./ProfileMenu.style.scss"

interface ProfileMenuProps {
  student?: StudentInfo | null
}

const ProfileMenu = ({ student: studentProp }: ProfileMenuProps) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const { student: authStudent, logout } = useLearnerAuth()
  const student = studentProp ?? authStudent
  const onProfileClick = useGuardLinkClick(STUDENT_PROFILE_PATH, () =>
    setOpen(false)
  )

  const displayName = [student?.first_name, student?.last_name]
    .filter(Boolean)
    .join(" ") || "Student"
  const subtitle = student?.grade ? `${student.grade}` : "Student"
  const initials = getStudentInitials(student?.first_name, student?.last_name)

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener("mousedown", handlePointerDown)
    return () => document.removeEventListener("mousedown", handlePointerDown)
  }, [open])

  const handleLogout = () => {
    logout({ redirect: true })
  }

  return (
    <div className="student-profile-menu" ref={rootRef}>
      <button
        type="button"
        className="student-profile-menu__trigger"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="User profile"
      >
        <div className="student-profile-menu__avatar">{initials}</div>
        <div className="student-profile-menu__meta">
          <p className="student-profile-menu__name">{displayName}</p>
          <p className="student-profile-menu__role">{subtitle}</p>
        </div>
      </button>

      {open && (
        <div className="student-profile-menu__dropdown" role="menu">
          <Link
            to={STUDENT_PROFILE_PATH}
            className="student-profile-menu__item"
            role="menuitem"
            onClick={onProfileClick}
          >
            <User className="student-profile-menu__item-icon" />
            View Profile
          </Link>
          <button
            type="button"
            className="student-profile-menu__item student-profile-menu__item--danger"
            role="menuitem"
            onClick={handleLogout}
          >
            <LogOut className="student-profile-menu__item-icon" />
            Logout
          </button>
        </div>
      )}
    </div>
  )
}

export default ProfileMenu
